import { useState } from "react";
import { Button, Checkbox, Input, Space, Typography, Alert, theme } from "antd";
import { ArrowLeftOutlined, SafetyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

interface MfaVerifyFormProps {
  isLoading: boolean;
  error: string | null;
  onVerify: (code: string, trustDevice: boolean) => void;
  onBack: () => void;
}

export const MfaVerifyForm: React.FC<MfaVerifyFormProps> = ({
  isLoading,
  error,
  onVerify,
  onBack,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [trustDevice, setTrustDevice] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  // Key to force re-mount Input.OTP after failed attempt
  const [otpKey, setOtpKey] = useState(0);

  const handleOtpComplete = (value: string) => {
    if (value.length === 6 && !isLoading) {
      onVerify(value, trustDevice);
      // Increment key to reset OTP input on next render (after error)
      setOtpKey((k) => k + 1);
    }
  };

  const handleBackupSubmit = () => {
    if (backupCode.trim() && !isLoading) {
      onVerify(backupCode.trim(), trustDevice);
      setBackupCode("");
    }
  };

  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <SafetyOutlined
          style={{ fontSize: 48, color: token.colorPrimary, marginBottom: 16 }}
        />
        <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
          {t("auth.mfa.verifyTitle")}
        </Title>
        <Text type="secondary">
          {useBackupCode
            ? t("auth.mfa.enterBackupCode")
            : t("auth.mfa.enterCode")}
        </Text>
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {useBackupCode ? (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Input
            placeholder={t("auth.mfa.backupCodePlaceholder")}
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
            onPressEnter={handleBackupSubmit}
            size="large"
            maxLength={8}
            style={{
              textAlign: "center",
              letterSpacing: 2,
              fontFamily: "monospace",
            }}
          />
          <Button
            type="primary"
            block
            size="large"
            loading={isLoading}
            onClick={handleBackupSubmit}
            disabled={!backupCode.trim()}
          >
            {t("auth.mfa.verify")}
          </Button>
          <Button type="link" block onClick={() => setUseBackupCode(false)}>
            {t("auth.mfa.useAuthenticator")}
          </Button>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Input.OTP
              key={otpKey}
              length={6}
              onChange={handleOtpComplete}
              disabled={isLoading}
              size="large"
            />
          </div>

          <Checkbox
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
          >
            {t("auth.mfa.trustDevice")}
          </Checkbox>

          <Button type="link" block onClick={() => setUseBackupCode(true)}>
            {t("auth.mfa.useBackupCode")}
          </Button>
        </Space>
      )}

      <div style={{ marginTop: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          block
        >
          {t("auth.mfa.backToLogin")}
        </Button>
      </div>
    </div>
  );
};
